import { AdminPanelSettings as PromoteIcon } from '@mui/icons-material'
import { Chip, IconButton } from '@mui/material'
import {
  Datagrid,
  DateField,
  EmailField,
  FunctionField,
  List,
  TextField,
  useDataProvider,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

const RoleField = ({ record }: { record: any }) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error'
      case 'vetadmin':
        return 'primary'
      case 'vet':
        return 'success'
      case 'siteadmin':
        return 'secondary'
      default:
        return 'default'
    }
  }

  return <Chip label={record.role} color={getRoleColor(record.role)} size="small" />
}

const PromoteButton = ({ record }: { record: any }) => {
  const { permissions } = usePermissions()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()

  // Only show for siteadmin and only for vet users
  if (permissions?.role !== 'siteadmin' || record.role !== 'vet') {
    return null
  }

  const handlePromote = async () => {
    if (!window.confirm(translate('pos.users.promote_confirm', { email: record.email }))) {
      return
    }

    try {
      // Use direct fetch instead of dataProvider to ensure correct HTTP method
      const response = await deadlineFetch(`/api/users/${record.id}`, {
        method: 'PATCH', // Payload CMS expects PATCH for updates
        headers: authHeaders(),
        body: JSON.stringify({
          role: 'vetadmin',
        }),
      })

      if (response.ok) {
        const result = await response.json()
        notify(translate('pos.users.promote_success', { email: record.email }), { type: 'success' })
        refresh()
      } else {
        const error = await response.json()
        console.error('Promotion error:', error)
        notify(error.errors?.[0]?.message || translate('pos.users.promote_failed'), {
          type: 'error',
        })
      }
    } catch (error) {
      console.error('Promotion exception:', error)
      notify(translate('pos.users.promote_failed'), { type: 'error' })
    }
  }

  return (
    <IconButton
      onClick={handlePromote}
      color="primary"
      size="small"
      title={translate('pos.users.promote_button')}
    >
      <PromoteIcon />
    </IconButton>
  )
}

const UserList = () => {
  const { permissions } = usePermissions()
  const translate = useTranslate()

  // Only siteadmin can access this resource
  if (permissions?.role !== 'siteadmin') {
    return <div>{translate('pos.users.access_denied')}</div>
  }

  return (
    <List
      filter={{ role: 'vet' }} // Only show vet users (can be promoted to vetadmin)
      sort={{ field: 'role', order: 'ASC' }}
    >
      <Datagrid rowClick={false}>
        <EmailField source="email" />
        <TextField source="name" />
        <FunctionField
          label={translate('pos.users.col_role')}
          render={(record: any) => <RoleField record={record} />}
        />
        <TextField source="clinic_name" label={translate('pos.users.col_clinic')} />
        <DateField source="createdAt" showTime />
        <FunctionField
          label={translate('pos.users.col_actions')}
          render={(record: any) => <PromoteButton record={record} />}
        />
      </Datagrid>
    </List>
  )
}

export default UserList
